import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Bell, Loader2, Settings, Clock, Truck, User, Package,
  AlertTriangle, CheckCircle, Save, RefreshCw, MapPin, Mail,
  Play, Pause, Send, History, Zap
} from "lucide-react";

export default function AlertasMensajeriaPage() {
  const [config, setConfig] = useState({
    activo: true,
    // Umbrales de tiempo
    dias_alerta_pendiente: 2,
    dias_alerta_en_proceso: 3,
    dias_alerta_sin_mensajero: 1,
    dias_alerta_no_completada: 5,
    // Configuración de notificaciones
    enviar_email: false,
    enviar_email_pendiente: false,
    enviar_email_en_proceso: false,
    enviar_email_sin_mensajero: false,
    enviar_email_no_completada: false,
    // Destinatarios por tipo de alerta
    destinatarios_email: [],
    destinatarios_pendiente: [],
    destinatarios_en_proceso: [],
    destinatarios_sin_mensajero: [],
    destinatarios_no_completada: [],
    // Frecuencia de alertas
    frecuencia_envio: "diario", // diario, cada_12h, cada_6h, inmediato
    hora_envio_diario: "08:00",
    // Escalamiento
    escalamiento_activo: false,
    dias_para_escalar: 3,
    destinatarios_escalamiento: [],
    // Dashboard
    mostrar_dashboard: true
  });
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("alertas");
  const [autoStatus, setAutoStatus] = useState({ auto_alert_enabled: false, task_running: false });
  const [toggling, setToggling] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "recepcion"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, alertasRes, statusRes] = await Promise.all([
        axios.get(`${API}/mensajeria/alertas/config`, getAuthHeaders()),
        axios.get(`${API}/mensajeria/alertas/pendientes`, getAuthHeaders()),
        axios.get(`${API}/mensajeria/alertas/auto/status`, getAuthHeaders())
      ]);
      setConfig(configRes.data);
      setAlertas(alertasRes.data.alertas || []);
      setAutoStatus(statusRes.data);
    } catch (err) {
      toast.error("Error al cargar datos de alertas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/mensajeria/alertas/config`, config, getAuthHeaders());
      toast.success("Configuración guardada exitosamente");
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAuto = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${API}/mensajeria/alertas/auto/toggle`, {}, getAuthHeaders());
      setAutoStatus(prev => ({ ...prev, auto_alert_enabled: res.data.auto_alert_enabled }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Error al cambiar estado de alertas automáticas");
    } finally {
      setToggling(false);
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      const res = await axios.post(`${API}/mensajeria/alertas/enviar-ahora`, {}, getAuthHeaders());
      toast.success(`${res.data.emails_enviados} emails enviados exitosamente`);
      fetchHistorial();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar alertas");
    } finally {
      setSendingNow(false);
    }
  };

  const fetchHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const res = await axios.get(`${API}/mensajeria/alertas/historial?limit=10`, getAuthHeaders());
      setHistorial(res.data);
    } catch (err) {
      console.error("Error fetching historial:", err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case "alta": return "bg-red-100 text-red-700 border-red-200";
      case "media": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-blue-100 text-blue-700 border-blue-200";
    }
  };

  const getAlertIcon = (tipo) => {
    switch (tipo) {
      case "pendiente_retrasado": return <Clock className="w-5 h-5 text-yellow-600" />;
      case "sin_mensajero": return <User className="w-5 h-5 text-orange-600" />;
      case "en_proceso_retrasado": return <Truck className="w-5 h-5 text-red-600" />;
      default: return <AlertTriangle className="w-5 h-5 text-slate-600" />;
    }
  };

  const formatTipoAlerta = (tipo) => {
    switch (tipo) {
      case "pendiente_retrasado": return "Pendiente retrasado";
      case "sin_mensajero": return "Sin mensajero";
      case "en_proceso_retrasado": return "En proceso retrasado";
      default: return tipo;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 overflow-auto">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Bell className="w-7 h-7 text-[#0C314C]" />
                Alertas de Mensajería
              </h1>
              <p className="text-slate-500 mt-1">
                Monitorea entregas con retraso y configura alertas automáticas
              </p>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="alertas" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alertas Activas ({alertas.length})
              </TabsTrigger>
              <TabsTrigger value="configuracion" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuración
              </TabsTrigger>
              <TabsTrigger value="automatico" className="flex items-center gap-2" onClick={() => fetchHistorial()}>
                <Zap className="w-4 h-4" />
                Sistema Automático
              </TabsTrigger>
            </TabsList>

            {/* Alertas Tab */}
            <TabsContent value="alertas">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
                </div>
              ) : alertas.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                      ¡Todo en orden!
                    </h3>
                    <p className="text-slate-500">
                      No hay entregas con retraso en este momento
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-yellow-700">
                            {alertas.filter(a => a.tipo === "pendiente_retrasado").length}
                          </p>
                          <p className="text-sm text-yellow-600">Pendientes retrasados</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-orange-200 bg-orange-50 dark:bg-orange-900/20">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-orange-700">
                            {alertas.filter(a => a.tipo === "sin_mensajero").length}
                          </p>
                          <p className="text-sm text-orange-600">Sin mensajero</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                          <Truck className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-700">
                            {alertas.filter(a => a.tipo === "en_proceso_retrasado").length}
                          </p>
                          <p className="text-sm text-red-600">En proceso retrasados</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Alerts List */}
                  <ScrollArea className="h-[calc(100vh-380px)] min-h-[300px]">
                    <div className="space-y-3 pr-4">
                      {alertas.map((alerta) => (
                        <Card 
                          key={alerta.id} 
                          className={`border-l-4 ${
                            alerta.prioridad === "alta" ? "border-l-red-500" : "border-l-amber-500"
                          } hover:shadow-md transition-shadow cursor-pointer`}
                          onClick={() => window.location.href = "/mensajeria/solicitudes"}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                alerta.tipo === "pendiente_retrasado" ? "bg-yellow-100" :
                                alerta.tipo === "sin_mensajero" ? "bg-orange-100" : "bg-red-100"
                              }`}>
                                {getAlertIcon(alerta.tipo)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <h4 className="font-semibold text-slate-800 dark:text-white">
                                      {alerta.cliente}
                                    </h4>
                                    <p className="text-sm text-slate-500">{alerta.titulo}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {alerta.es_urgente && (
                                      <Badge className="bg-red-500 text-white">URGENTE</Badge>
                                    )}
                                    <Badge className={getPriorityColor(alerta.prioridad)}>
                                      {alerta.prioridad === "alta" ? "Alta" : "Media"}
                                    </Badge>
                                  </div>
                                </div>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                                  {alerta.descripcion}
                                </p>
                                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {alerta.direccion || "Sin dirección"}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Package className="w-3 h-3" />
                                    {alerta.tipo_documento}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {formatTipoAlerta(alerta.tipo)}
                                  </Badge>
                                  <span className={`font-medium ${
                                    alerta.dias_retraso >= alerta.umbral_dias * 2 ? "text-red-600" : "text-amber-600"
                                  }`}>
                                    {alerta.dias_retraso} días de retraso
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </TabsContent>

            {/* Configuración Tab */}
            <TabsContent value="configuracion">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <div className="space-y-6 pr-4">
                  {/* Card Principal - Sistema Activo */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Configuración General
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <Label className="text-base font-medium">Sistema de Alertas Activo</Label>
                          <p className="text-sm text-slate-500">
                            Habilita o deshabilita todas las alertas de mensajería
                          </p>
                        </div>
                        <Switch
                          checked={config.activo}
                          onCheckedChange={(checked) => setConfig({...config, activo: checked})}
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <Label className="text-base font-medium">Mostrar en Dashboard</Label>
                          <p className="text-sm text-slate-500">
                            Muestra un resumen de alertas en el panel de notificaciones
                          </p>
                        </div>
                        <Switch
                          checked={config.mostrar_dashboard}
                          onCheckedChange={(checked) => setConfig({...config, mostrar_dashboard: checked})}
                          disabled={!isAdmin}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card - Umbrales de Tiempo */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Clock className="w-5 h-5 text-blue-600" />
                        Umbrales de Tiempo para Alertas
                      </CardTitle>
                      <CardDescription>
                        Define cuántos días deben pasar antes de generar una alerta
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Pendiente */}
                        <div className="p-4 border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-5 h-5 text-yellow-600" />
                            <Label className="font-semibold text-yellow-800 dark:text-yellow-300">Pendiente</Label>
                          </div>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={config.dias_alerta_pendiente}
                            onChange={(e) => setConfig({...config, dias_alerta_pendiente: parseInt(e.target.value) || 1})}
                            disabled={!isAdmin}
                            className="mb-2"
                          />
                          <p className="text-xs text-yellow-700 dark:text-yellow-400">
                            Días en estado pendiente antes de alertar
                          </p>
                        </div>

                        {/* En Proceso */}
                        <div className="p-4 border border-blue-200 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <Truck className="w-5 h-5 text-blue-600" />
                            <Label className="font-semibold text-blue-800 dark:text-blue-300">En Proceso</Label>
                          </div>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={config.dias_alerta_en_proceso}
                            onChange={(e) => setConfig({...config, dias_alerta_en_proceso: parseInt(e.target.value) || 1})}
                            disabled={!isAdmin}
                            className="mb-2"
                          />
                          <p className="text-xs text-blue-700 dark:text-blue-400">
                            Días asignado sin completar antes de alertar
                          </p>
                        </div>

                        {/* Sin Mensajero */}
                        <div className="p-4 border border-orange-200 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <User className="w-5 h-5 text-orange-600" />
                            <Label className="font-semibold text-orange-800 dark:text-orange-300">Sin Mensajero</Label>
                          </div>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={config.dias_alerta_sin_mensajero}
                            onChange={(e) => setConfig({...config, dias_alerta_sin_mensajero: parseInt(e.target.value) || 1})}
                            disabled={!isAdmin}
                            className="mb-2"
                          />
                          <p className="text-xs text-orange-700 dark:text-orange-400">
                            Días sin asignar mensajero antes de alertar
                          </p>
                        </div>

                        {/* No Completada */}
                        <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <Label className="font-semibold text-red-800 dark:text-red-300">No Completada</Label>
                          </div>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={config.dias_alerta_no_completada || 5}
                            onChange={(e) => setConfig({...config, dias_alerta_no_completada: parseInt(e.target.value) || 5})}
                            disabled={!isAdmin}
                            className="mb-2"
                          />
                          <p className="text-xs text-red-700 dark:text-red-400">
                            Días totales sin completar (crítico)
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card - Notificaciones por Email */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Mail className="w-5 h-5 text-purple-600" />
                        Notificaciones por Email
                      </CardTitle>
                      <CardDescription>
                        Configura qué alertas envían email y a quién
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Alerta Pendiente */}
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-yellow-600" />
                            <Label className="font-medium">Alertas de Pendientes</Label>
                          </div>
                          <Switch
                            checked={config.enviar_email_pendiente || false}
                            onCheckedChange={(checked) => setConfig({...config, enviar_email_pendiente: checked})}
                            disabled={!isAdmin}
                          />
                        </div>
                        {config.enviar_email_pendiente && (
                          <div className="mt-3 pl-6 space-y-2">
                            <Label className="text-sm text-slate-600">Destinatarios (emails separados por coma)</Label>
                            <Input
                              placeholder="email1@empresa.com, email2@empresa.com"
                              value={(config.destinatarios_pendiente || []).join(", ")}
                              onChange={(e) => setConfig({...config, destinatarios_pendiente: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                              disabled={!isAdmin}
                            />
                          </div>
                        )}
                      </div>

                      {/* Alerta En Proceso */}
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-blue-600" />
                            <Label className="font-medium">Alertas de En Proceso</Label>
                          </div>
                          <Switch
                            checked={config.enviar_email_en_proceso || false}
                            onCheckedChange={(checked) => setConfig({...config, enviar_email_en_proceso: checked})}
                            disabled={!isAdmin}
                          />
                        </div>
                        {config.enviar_email_en_proceso && (
                          <div className="mt-3 pl-6 space-y-2">
                            <Label className="text-sm text-slate-600">Destinatarios (emails separados por coma)</Label>
                            <Input
                              placeholder="email1@empresa.com, email2@empresa.com"
                              value={(config.destinatarios_en_proceso || []).join(", ")}
                              onChange={(e) => setConfig({...config, destinatarios_en_proceso: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                              disabled={!isAdmin}
                            />
                          </div>
                        )}
                      </div>

                      {/* Alerta Sin Mensajero */}
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-orange-600" />
                            <Label className="font-medium">Alertas Sin Mensajero</Label>
                          </div>
                          <Switch
                            checked={config.enviar_email_sin_mensajero || false}
                            onCheckedChange={(checked) => setConfig({...config, enviar_email_sin_mensajero: checked})}
                            disabled={!isAdmin}
                          />
                        </div>
                        {config.enviar_email_sin_mensajero && (
                          <div className="mt-3 pl-6 space-y-2">
                            <Label className="text-sm text-slate-600">Destinatarios (emails separados por coma)</Label>
                            <Input
                              placeholder="email1@empresa.com, email2@empresa.com"
                              value={(config.destinatarios_sin_mensajero || []).join(", ")}
                              onChange={(e) => setConfig({...config, destinatarios_sin_mensajero: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                              disabled={!isAdmin}
                            />
                          </div>
                        )}
                      </div>

                      {/* Alerta No Completada (Crítica) */}
                      <div className="p-4 border border-red-200 bg-red-50/50 dark:bg-red-900/10 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                            <Label className="font-medium text-red-800 dark:text-red-300">Alertas Críticas (No Completadas)</Label>
                            <Badge variant="destructive" className="text-xs">CRÍTICO</Badge>
                          </div>
                          <Switch
                            checked={config.enviar_email_no_completada || false}
                            onCheckedChange={(checked) => setConfig({...config, enviar_email_no_completada: checked})}
                            disabled={!isAdmin}
                          />
                        </div>
                        {config.enviar_email_no_completada && (
                          <div className="mt-3 pl-6 space-y-2">
                            <Label className="text-sm text-red-700 dark:text-red-400">Destinatarios para alertas críticas</Label>
                            <Input
                              placeholder="gerencia@empresa.com, supervisor@empresa.com"
                              value={(config.destinatarios_no_completada || []).join(", ")}
                              onChange={(e) => setConfig({...config, destinatarios_no_completada: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                              disabled={!isAdmin}
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card - Frecuencia de Envío */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <RefreshCw className="w-5 h-5 text-green-600" />
                        Frecuencia de Envío
                      </CardTitle>
                      <CardDescription>
                        Configura cada cuánto se envían las alertas por email
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label>Frecuencia de alertas</Label>
                          <select
                            value={config.frecuencia_envio || "diario"}
                            onChange={(e) => setConfig({...config, frecuencia_envio: e.target.value})}
                            disabled={!isAdmin}
                            className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-600 dark:bg-slate-700"
                          >
                            <option value="inmediato">Inmediato (al detectar alerta)</option>
                            <option value="cada_6h">Cada 6 horas</option>
                            <option value="cada_12h">Cada 12 horas</option>
                            <option value="diario">Diario</option>
                          </select>
                        </div>
                        {config.frecuencia_envio === "diario" && (
                          <div className="space-y-2">
                            <Label>Hora de envío diario</Label>
                            <Input
                              type="time"
                              value={config.hora_envio_diario || "08:00"}
                              onChange={(e) => setConfig({...config, hora_envio_diario: e.target.value})}
                              disabled={!isAdmin}
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card - Escalamiento */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        Escalamiento de Alertas
                      </CardTitle>
                      <CardDescription>
                        Configura escalamiento automático cuando las alertas persisten
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <div>
                          <Label className="text-base font-medium">Activar Escalamiento</Label>
                          <p className="text-sm text-slate-500">
                            Escala a superiores si la alerta no se resuelve
                          </p>
                        </div>
                        <Switch
                          checked={config.escalamiento_activo || false}
                          onCheckedChange={(checked) => setConfig({...config, escalamiento_activo: checked})}
                          disabled={!isAdmin}
                        />
                      </div>

                      {config.escalamiento_activo && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                          <div className="space-y-2">
                            <Label>Días para escalar</Label>
                            <Input
                              type="number"
                              min="1"
                              max="30"
                              value={config.dias_para_escalar || 3}
                              onChange={(e) => setConfig({...config, dias_para_escalar: parseInt(e.target.value) || 3})}
                              disabled={!isAdmin}
                            />
                            <p className="text-xs text-slate-500">
                              Días adicionales antes de escalar la alerta
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label>Destinatarios de escalamiento</Label>
                            <Input
                              placeholder="gerencia@empresa.com, director@empresa.com"
                              value={(config.destinatarios_escalamiento || []).join(", ")}
                              onChange={(e) => setConfig({...config, destinatarios_escalamiento: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})}
                              disabled={!isAdmin}
                            />
                            <p className="text-xs text-slate-500">
                              Emails separados por coma
                            </p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Save Button */}
                  {isAdmin && (
                    <div className="flex justify-end pt-4 border-t sticky bottom-0 bg-slate-50 dark:bg-slate-900 py-4">
                      <Button 
                        onClick={handleSaveConfig}
                        disabled={saving}
                        className="bg-[#0C314C] hover:bg-[#2d5a8a] text-white"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Guardar Configuración
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Sistema Automático Tab */}
            <TabsContent value="automatico">
              <div className="space-y-6">
                {/* Control Panel */}
                <Card className={autoStatus.auto_alert_enabled ? "border-green-200 bg-green-50/50" : "border-slate-200"}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Zap className={`w-5 h-5 ${autoStatus.auto_alert_enabled ? 'text-green-600' : 'text-slate-400'}`} />
                        Sistema de Alertas Automáticas
                      </span>
                      <Badge variant={autoStatus.auto_alert_enabled ? "default" : "secondary"} className={autoStatus.auto_alert_enabled ? "bg-green-600" : ""}>
                        {autoStatus.auto_alert_enabled ? "Activo" : "Inactivo"}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Envío automático de alertas por email según la configuración establecida
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      <Button
                        onClick={handleToggleAuto}
                        disabled={toggling || !isAdmin}
                        variant={autoStatus.auto_alert_enabled ? "destructive" : "default"}
                        className={!autoStatus.auto_alert_enabled ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {toggling ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : autoStatus.auto_alert_enabled ? (
                          <Pause className="w-4 h-4 mr-2" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        {autoStatus.auto_alert_enabled ? "Detener Sistema" : "Iniciar Sistema"}
                      </Button>
                      
                      <Button
                        onClick={handleSendNow}
                        disabled={sendingNow || !isAdmin}
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        {sendingNow ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Enviar Alertas Ahora
                      </Button>
                    </div>
                    
                    {autoStatus.auto_alert_enabled && (
                      <div className="mt-4 p-4 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <p className="text-sm text-green-800 dark:text-green-300 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          El sistema está enviando alertas automáticamente según la frecuencia configurada: <strong>{config.frecuencia_envio || "diario"}</strong>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Status Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Settings className="w-5 h-5" />
                      Resumen de Configuración
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-lg border ${autoStatus.enviar_email_pendiente ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-yellow-600" />
                          <span className="font-medium text-sm">Pendientes</span>
                        </div>
                        <Badge variant={autoStatus.enviar_email_pendiente ? "default" : "secondary"} className={autoStatus.enviar_email_pendiente ? "bg-yellow-600" : ""}>
                          {autoStatus.enviar_email_pendiente ? "Email Activo" : "Desactivado"}
                        </Badge>
                      </div>
                      
                      <div className={`p-4 rounded-lg border ${autoStatus.enviar_email_sin_mensajero ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-orange-600" />
                          <span className="font-medium text-sm">Sin Mensajero</span>
                        </div>
                        <Badge variant={autoStatus.enviar_email_sin_mensajero ? "default" : "secondary"} className={autoStatus.enviar_email_sin_mensajero ? "bg-orange-600" : ""}>
                          {autoStatus.enviar_email_sin_mensajero ? "Email Activo" : "Desactivado"}
                        </Badge>
                      </div>
                      
                      <div className={`p-4 rounded-lg border ${autoStatus.enviar_email_en_proceso ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-sm">En Proceso</span>
                        </div>
                        <Badge variant={autoStatus.enviar_email_en_proceso ? "default" : "secondary"} className={autoStatus.enviar_email_en_proceso ? "bg-blue-600" : ""}>
                          {autoStatus.enviar_email_en_proceso ? "Email Activo" : "Desactivado"}
                        </Badge>
                      </div>
                      
                      <div className={`p-4 rounded-lg border ${autoStatus.enviar_email_no_completada ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="font-medium text-sm">No Completada</span>
                        </div>
                        <Badge variant={autoStatus.enviar_email_no_completada ? "default" : "secondary"} className={autoStatus.enviar_email_no_completada ? "bg-red-600" : ""}>
                          {autoStatus.enviar_email_no_completada ? "Email Activo" : "Desactivado"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Historial */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <History className="w-5 h-5" />
                      Historial de Envíos
                    </CardTitle>
                    <CardDescription>
                      Últimas ejecuciones del sistema de alertas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingHistorial ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : historial.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No hay historial de envíos</p>
                    ) : (
                      <div className="space-y-3">
                        {historial.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.tipo === 'manual' ? 'bg-blue-100' : 'bg-green-100'}`}>
                                {item.tipo === 'manual' ? (
                                  <Send className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <Zap className="w-5 h-5 text-green-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {item.tipo === 'manual' ? `Envío manual por ${item.ejecutado_por || 'usuario'}` : 'Envío automático'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {new Date(item.timestamp).toLocaleString('es-DO')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                                {item.alertas_pendiente || 0} pend.
                              </span>
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                                {item.alertas_sin_mensajero || 0} s/m
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                {item.alertas_en_proceso || 0} proc.
                              </span>
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                                {item.alertas_no_completada || 0} crit.
                              </span>
                              <Badge className="bg-green-600">
                                {item.emails_enviados} emails
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
